/**
 * Application routing configuration
 * @author Maheen Nasir
 * @description Defines the application routes with lazy loading and suspense handling
 */

import { lazy, Suspense } from "react";
import { type RouteObject } from "react-router-dom";
import type { ReactElement, ComponentType } from "react";
import Spinner from "@components/common/Spinner";
import { BROWSER_ROUTES } from "@constants/browser.routes";
import AuthGuard from "@components/RouteGuards/AuthGuard";
import GuestGuard from "@components/RouteGuards/GuestGuard";
import ResetRouteGaurd from "@components/RouteGuards/ResetGuard";
import { MainLayout } from "@components/Layout/MainLayout";

// Lazy load components
const NotFound = lazy(() => import("@pages/404"));
const Unauthorized = lazy(() => import("@pages/Unauthorized"));
const InternalServerError = lazy(() => import("@pages/500"));
const Login = lazy(() => import("@pages/Login"));
const ForgetPassword = lazy(() => import("@pages/ForgetPassword"));
const VerifyOtp = lazy(() => import("@pages/VerifyOtp"));
const ResetPassword = lazy(() => import("@pages/ResetPassword"));
const SetPassword = lazy(() => import("@pages/SetPassword"));

const PrivacyPolicy = lazy(() => import("@pages/PrivacyPolicy"));
const TermsAndConditions = lazy(() => import("@pages/TermsAndConditions"));

// Main page components
const Dashboard = lazy(() => import("@pages/Dashboard"));
const Institutes = lazy(() => import("@pages/Institutes"));
const Attendance = lazy(() => import("@pages/Attendance"));
const Students = lazy(() => import("@pages/Students"));

/**
 * Higher-order component to wrap lazy-loaded components with Suspense
 * @param {React.LazyExoticComponent<ComponentType>} Component - The lazy-loaded component to wrap
 * @returns {JSX.Element} The wrapped component with Suspense
 */
const withSuspense = (Component: React.LazyExoticComponent<ComponentType>): ReactElement => {
	return (
		<Suspense fallback={<Spinner overlay />}>
			<Component />
		</Suspense>
	);
};

/**
 * Application route configuration
 * @description Defines the main routes of the application with their corresponding components
 */
export const routes: RouteObject[] = [
	{
		path: "/",
		element: (
			<AuthGuard>
				<MainLayout />
			</AuthGuard>
		),
		children: [
			{ index: true, element: withSuspense(Dashboard) },
			{ path: BROWSER_ROUTES.INSTITUTES, element: withSuspense(Institutes) },
			{ path: BROWSER_ROUTES.ATTENDANCE, element: withSuspense(Attendance) },
			{ path: BROWSER_ROUTES.STUDENTS, element: withSuspense(Students) },
		],
	},
	{
		path: BROWSER_ROUTES.LOGIN,
		element: <GuestGuard>{withSuspense(Login)}</GuestGuard>,
	},
	{
		path: BROWSER_ROUTES.FORGET_PASSWORD,
		element: <GuestGuard>{withSuspense(ForgetPassword)}</GuestGuard>,
	},
	{
		path: BROWSER_ROUTES.VERIFY_OTP,
		element: <GuestGuard>{withSuspense(VerifyOtp)}</GuestGuard>,
	},
	{
		path: BROWSER_ROUTES.RESET_PASSWORD,
		element: (
			<GuestGuard>
				<ResetRouteGaurd>{withSuspense(ResetPassword)}</ResetRouteGaurd>
			</GuestGuard>
		),
	},
	{
		path: BROWSER_ROUTES.SET_PASSWORD,
		element: <GuestGuard>{withSuspense(SetPassword)}</GuestGuard>,
	},
	{
		path: BROWSER_ROUTES.UNAUTHORIZED,
		element: withSuspense(Unauthorized),
	},
	{
		path: BROWSER_ROUTES.SERVER_ERROR,
		element: withSuspense(InternalServerError),
	},
	{
		path: BROWSER_ROUTES.PRIVACY_POLICY,
		element: withSuspense(PrivacyPolicy),
	},
	{
		path: BROWSER_ROUTES.TERMS_AND_CONDITIONS,
		element: withSuspense(TermsAndConditions),
	},
	{
		path: "*",
		element: withSuspense(NotFound),
	},
];

export default routes;
